import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button,
  useTheme,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/system';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Link as RouterLink, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&:after': {
    content: '""',
    display: 'block',
    width: '80px',
    height: '4px',
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(1),
  }
}));

const ThankYouCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(6),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  boxShadow: theme.shadows[3]
}));

const IconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.main,
  borderRadius: '50%',
  width: 80,
  height: 80,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(3)
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.shape.borderRadius,
  fontWeight: 600,
  marginTop: theme.spacing(2)
}));

const ImpactPoint = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  '& svg': {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2)
  }
}));

const ThankYou = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const [verified, setVerified] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { t, i18n } = useTranslation();

  // Function to generate language-aware links
  const getLanguagePath = (path) => {
    const currentLang = lang || 'en';
    return `/${currentLang}${path}`;
  };

  // Debug logging
  console.log('ThankYou component - Current language:', i18n.language);
  console.log('ThankYou component - URL pathname:', location.pathname);
  console.log('ThankYou component - Translation test:', t('thankYou.title'));

  useEffect(() => {
    const verifyDonation = async () => {
      try {
        // Get the session_id from URL query parameters
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        
        if (!sessionId) {
          // No session ID, redirect to home page
          setError(t('thankYou.invalid_donation_session_error'));
          setTimeout(() => {
            navigate('/');
          }, 3000);
          return;
        }
        
        // Verify the session with your backend (optional)
        // This would add an extra layer of security
        // by checking if the session ID is valid and completed
        /*
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/donation/verify?session_id=${sessionId}`);
        
        if (!response.ok) {
          throw new Error('Unable to verify donation');
        }
        
        const data = await response.json();
        setDonationDetails(data);
        */
        
        // For now, just verify that session_id exists and save it to localStorage 
        // to remember this was a valid donation
        localStorage.setItem('donation_completed', 'true');
        localStorage.setItem('donation_session_id', sessionId);
        
        setVerified(true);
      } catch (err) {
        console.error('Verification error:', err);
        setError(t('thankYou.donation_verification_error'));
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    // Check if we have a previous verified donation in localStorage
    const hasCompletedDonation = localStorage.getItem('donation_completed') === 'true';
    
    if (hasCompletedDonation) {
      // Already verified in a previous visit
      setVerified(true);
      setLoading(false);
    } else {
      // Need to verify from URL parameters
      verifyDonation();
    }
  }, [location, navigate, t]);

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to={getLanguagePath('/')}
        >
          {t('thankYou.return_to_homepage')}
        </Button>
      </Container>
    );
  }

  if (!verified) {
    return (
      <Container sx={{ py: 8 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('thankYou.donation_required_warning')}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          component={RouterLink}
          to={getLanguagePath('/')}
        >
          {t('thankYou.return_to_homepage')}
        </Button>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box 
        sx={{ 
          py: 8, 
          backgroundColor: theme.palette.background.paper 
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={10} textAlign="center">
              <SectionTitle variant="h3" align="center">
                {t('thankYou.thank_you_for_donation')}
              </SectionTitle>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 4,
                  color: 'text.secondary',
                  fontWeight: 500
                }}
              >
                {t('thankYou.your_generosity_helps_bridge_digital_divide')}
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Thank You Confirmation */}
      <Container maxWidth="md" sx={{ py: 8 }}>
        <ThankYouCard>
          <IconContainer>
            <CheckCircleOutlineIcon sx={{ fontSize: 50 }} />
          </IconContainer>
          <Typography variant="h4" gutterBottom fontWeight={700}>
            {t('thankYou.donation_successful')}
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
            {t('thankYou.we_received_your_generous_contribution')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('thankYou.confirmation_receipt_sent_to_email')}
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ActionButton 
              variant="contained" 
              color="primary" 
              component={RouterLink} 
              to={getLanguagePath('/')}
            >
              {t('thankYou.return_to_homepage')}
            </ActionButton>
            <ActionButton 
              variant="outlined" 
              color="primary" 
              component={RouterLink} 
              to={getLanguagePath('/programs')}
            >
              {t('thankYou.explore_our_programs')}
            </ActionButton>
          </Box>
        </ThankYouCard>

        <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, textAlign: 'center' }}>
          {t('thankYou.your_donation_makes_real_impact')}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <ImpactPoint>
              <FavoriteIcon />
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {t('thankYou.technology_access_for_communities')}
                </Typography>
                <Typography variant="body2">
                  {t('thankYou.providing_computers_tablets_internet_access')}
                </Typography>
              </Box>
            </ImpactPoint>
          </Grid>
          <Grid item xs={12}>
            <ImpactPoint>
              <FavoriteIcon />
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {t('thankYou.digital_skills_training')}
                </Typography>
                <Typography variant="body2">
                  {t('thankYou.funding_programs_that_teach_essential_digital_literacy_skills')}
                </Typography>
              </Box>
            </ImpactPoint>
          </Grid>
          <Grid item xs={12}>
            <ImpactPoint>
              <FavoriteIcon />
              <Box>
                <Typography variant="body1" fontWeight={600}>
                  {t('thankYou.sustainable_agricultural_technology')}
                </Typography>
                <Typography variant="body2">
                  {t('thankYou.supporting_agricultural_technology_initiatives_that_improve_farming_efficiency_and_sustainability')}
                </Typography>
              </Box>
            </ImpactPoint>
          </Grid>
        </Grid>

        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('thankYou.share_your_support')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('thankYou.help_us_spread_the_word_about_our_mission_by_sharing_on_social_media')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ 
                backgroundColor: '#1877F2', 
                '&:hover': { backgroundColor: '#0E5FC0' } 
              }}
              href="https://www.facebook.com/sharer/sharer.php?u=https://digisparkfoundation.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('thankYou.facebook')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ 
                backgroundColor: '#1DA1F2', 
                '&:hover': { backgroundColor: '#0C85D0' } 
              }}
              href="https://twitter.com/intent/tweet?text=I just supported Digispark Foundation's mission to bridge the digital divide in Africa. Join me! https://digisparkfoundation.org"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('thankYou.twitter')}
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ThankYou; 